
import Chart from 'chart.js/auto';
import Vue from 'vue';
import { api, BudgetAllocation, BudgetBucket, ExpectedIncome, InsightsPeriod, InsightsReport, OutputExpense, SavingsForecast, SavingsForecastRow, TagSpend } from '@/services/api';
import { visualizeAmount } from '@/services/visualizer';
import { formatDate } from '@/services/date';
import SpendingDecisions from './SpendingDecisions.vue';

type InsightTab = "general" | "categories" | "budgets" | "review" | "decisions";

type BudgetRow = {
    id: number | string;
    name: string;
    slug: string;
    periodLimitCents: number;
    spentCents: number;
    projectedCents: number;
    remainingCents: number;
    projectedDeltaCents: number;
};

const budgetChartColors = [
    "rgb(82, 130, 145)",
    "rgb(166, 115, 82)",
    "rgb(104, 139, 92)",
    "rgb(145, 93, 103)",
    "rgb(117, 112, 153)",
    "rgb(153, 135, 78)"
];

export default Vue.extend({
    components: {
        SpendingDecisions
    },
    data() {
        return {
            report: null as InsightsReport | null,
            selectedLabel: "",
            selectedYearLabel: new Date().getFullYear().toString(),
            selectedPeriodKind: "year" as "year" | "quick",
            activeTab: "general" as InsightTab,
            tabs: [
                { key: "general" as InsightTab, label: "General" },
                { key: "categories" as InsightTab, label: "Categories" },
                { key: "budgets" as InsightTab, label: "Budgets" },
                { key: "review" as InsightTab, label: "Review" },
                { key: "decisions" as InsightTab, label: "Decisions" }
            ],
            expenses: [] as OutputExpense[],
            budgetBuckets: [] as BudgetBucket[],
            budgetAllocations: [] as BudgetAllocation[],
            expectedIncome: null as ExpectedIncome | null,
            savingsForecast: null as SavingsForecast | null,
            expectedIncomeDraft: "",
            savingExpectedIncome: false,
            expectedIncomeMessage: "",
            periodNetChart: null as { destroy(): void } | null,
            savingsForecastChart: null as { destroy(): void } | null,
            budgetAllocationChart: null as { destroy(): void } | null,
            unallocatedSpendChart: null as { destroy(): void } | null
        }
    },
    async mounted() {
        await this.refresh();
    },
    beforeDestroy() {
        if (this.periodNetChart) {
            this.periodNetChart.destroy();
        }
        if (this.savingsForecastChart) {
            this.savingsForecastChart.destroy();
        }
        if (this.budgetAllocationChart) {
            this.budgetAllocationChart.destroy();
        }
        if (this.unallocatedSpendChart) {
            this.unallocatedSpendChart.destroy();
        }
    },
    watch: {
        selectedLabel() {
            this.renderChartsSoon();
        },
        selectedYearLabel() {
            this.loadExpectedIncome();
            this.renderChartsSoon();
        },
        selectedPeriodKind() {
            this.renderChartsSoon();
        },
        activeTab() {
            this.renderChartsSoon();
        }
    },
    methods: {
        async refresh() {
            const [report, expenses, budgetBuckets, budgetAllocations] = await Promise.all([
                api.getInsights(),
                api.listExpenses(),
                api.getBudgetBuckets(),
                api.getBudgetAllocations()
            ]);
            this.report = report;
            this.expenses = expenses;
            this.budgetBuckets = budgetBuckets;
            this.budgetAllocations = budgetAllocations;
            if (this.report) {
                const currentYear = new Date().getFullYear().toString();
                if (!this.yearOptions.includes(this.selectedYearLabel)) {
                    this.selectedYearLabel = this.yearOptions.includes(currentYear)
                        ? currentYear
                        : this.yearOptions[0] || "";
                }
                if (!this.selectedPeriod) {
                    this.selectedPeriodKind = this.yearOptions.length > 0 ? "year" : "quick";
                    this.selectedLabel = this.quickPeriods[0]?.label || "";
                }
            }
            await this.$nextTick();
            await this.loadExpectedIncome();
            this.renderPeriodNetChart();
            this.renderSavingsForecastChart();
            this.renderBudgetAllocationChart();
            this.renderUnallocatedSpendChart();
        },
        async loadExpectedIncome() {
            if (!this.selectedYearNumber) {
                this.expectedIncome = null;
                this.savingsForecast = null;
                this.expectedIncomeDraft = "";
                return;
            }

            const [expectedIncome, savingsForecast] = await Promise.all([
                api.getExpectedIncome(this.selectedYearNumber),
                api.getSavingsForecast(this.selectedYearNumber)
            ]);
            this.expectedIncome = expectedIncome;
            this.savingsForecast = savingsForecast;
            this.expectedIncomeDraft = expectedIncome?.expectedIncomeCents == null
                ? ""
                : (expectedIncome.expectedIncomeCents / 100).toFixed(2);
            this.expectedIncomeMessage = "";
            this.renderChartsSoon();
        },
        async saveExpectedIncome() {
            if (!this.selectedYearNumber) {
                return;
            }

            this.savingExpectedIncome = true;
            this.expectedIncomeMessage = "";
            try {
                const saved = await api.setExpectedIncome({
                    year: this.selectedYearNumber,
                    expectedIncomeCents: this.parseMoneyToCents(this.expectedIncomeDraft)
                });
                this.expectedIncome = saved;
                this.expectedIncomeDraft = saved?.expectedIncomeCents == null
                    ? ""
                    : (saved.expectedIncomeCents / 100).toFixed(2);
                this.savingsForecast = await api.getSavingsForecast(this.selectedYearNumber);
                this.expectedIncomeMessage = "Saved";
                this.renderChartsSoon();
            } finally {
                this.savingExpectedIncome = false;
            }
        },
        parseMoneyToCents(value: string): number | null {
            const normalized = value.trim().replace(",", ".");
            if (!normalized) {
                return null;
            }
            const parsed = Number(normalized);
            if (!Number.isFinite(parsed)) {
                return null;
            }
            return Math.round(parsed * 100);
        },
        renderChartsSoon() {
            this.$nextTick(() => {
                this.renderPeriodNetChart();
                this.renderSavingsForecastChart();
                this.renderBudgetAllocationChart();
                this.renderUnallocatedSpendChart();
            });
        },
        money(amount: number): string {
            return visualizeAmount(amount);
        },
        formatDate(date: string): string {
            return formatDate(new Date(date));
        },
        monthLabel(date: string): string {
            const month = new Date(date);
            return `${month.getFullYear()}-${(month.getMonth() + 1).toString().padStart(2, "0")}`;
        },
        barWidth(amount: number, max: number): string {
            if (max <= 0) {
                return "0%";
            }
            return `${Math.max(2, Math.round((amount / max) * 100))}%`;
        },
        netAmount(tag: TagSpend): number {
            return tag.netAmount == null ? tag.amount + tag.refundAmount : tag.netAmount;
        },
        selectYear() {
            this.selectedPeriodKind = "year";
            if (!this.selectedYearLabel && this.yearOptions.length > 0) {
                this.selectedYearLabel = this.yearOptions[0];
            }
        },
        selectQuickPeriod(label: string) {
            this.selectedPeriodKind = "quick";
            this.selectedLabel = label;
        },
        isReportableBudgetExpense(expense: OutputExpense): boolean {
            return expense.amount !== 0
                && expense.tags.includes("exclude-from-spending") === false
                && (expense.amount > 0 || (expense.tags.includes("income") === false && expense.tags.includes("stipendio") === false));
        },
        periodLimitForBucket(bucketId: number): number {
            return this.budgetAllocations
                .filter(allocation => allocation.budgetBucketId === bucketId)
                .reduce((total, allocation) => {
                    const monthCount = this.budgetMonthsByYear[allocation.year] || 0;
                    if (monthCount === 0) {
                        return total;
                    }
                    if (allocation.monthlyLimitCents != null) {
                        return total + allocation.monthlyLimitCents * monthCount;
                    }
                    return total + Math.round((allocation.annualLimitCents || 0) * (monthCount / 12));
                }, 0);
        },
        renderPeriodNetChart() {
            const canvas = this.$refs.periodNetCanvas as HTMLCanvasElement | undefined;
            if (!canvas) {
                return;
            }

            const months = this.periodMonths;
            const buckets = months.map(month => this.monthNet(month.start, month.end));

            if (this.periodNetChart) {
                this.periodNetChart.destroy();
            }

            this.periodNetChart = new Chart(canvas, {
                type: "bar",
                data: {
                    labels: months.map(month => month.label),
                    datasets: [
                        {
                            label: "Monthly net",
                            backgroundColor: "rgb(82, 130, 145)",
                            borderColor: "rgb(82, 130, 145)",
                            data: buckets.map(amount => amount / 100)
                        }
                    ]
                },
                options: {
                    indexAxis: "y",
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }
            });
        },
        renderSavingsForecastChart() {
            const canvas = this.$refs.savingsForecastCanvas as HTMLCanvasElement | undefined;
            if (!canvas) {
                return;
            }

            const rows = this.savingsForecastRows;
            if (this.savingsForecastChart) {
                this.savingsForecastChart.destroy();
            }

            this.savingsForecastChart = new Chart(canvas, {
                type: "line",
                data: {
                    labels: rows.map(row => row.month),
                    datasets: [
                        {
                            label: "Projected savings",
                            data: rows.map(row => row.projectedSavingsCents / 100),
                            borderColor: "rgb(82, 130, 145)",
                            backgroundColor: "rgba(82, 130, 145, 0.14)",
                            tension: 0.25,
                            fill: false
                        },
                        {
                            label: "Actual savings so far",
                            data: rows.map(row => row.actualSavingsCents / 100),
                            borderColor: "rgb(166, 115, 82)",
                            backgroundColor: "rgba(166, 115, 82, 0.14)",
                            tension: 0.25,
                            fill: false
                        },
                        {
                            label: `${this.money(this.savingsForecast?.savingsTargetCents || 500000)} target`,
                            data: rows.map(row => row.savingsTargetCents / 100),
                            borderColor: "rgba(145, 93, 103, 0.8)",
                            borderDash: [6, 6],
                            pointRadius: 0,
                            tension: 0,
                            fill: false
                        }
                    ]
                },
                options: {
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: "index"
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (context: { dataset: { label?: string }; parsed: { y: number } }) =>
                                    `${context.dataset.label}: ${this.money(Math.round(context.parsed.y * 100))}`
                            }
                        }
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: (value: string | number) => this.money(Number(value) * 100)
                            }
                        }
                    }
                }
            });
        },
        renderBudgetAllocationChart() {
            const canvas = this.$refs.budgetAllocationCanvas as HTMLCanvasElement | undefined;
            if (!canvas) {
                return;
            }

            const rows = this.budgetRows.filter(row => row.periodLimitCents > 0);
            if (this.budgetAllocationChart) {
                this.budgetAllocationChart.destroy();
            }

            this.budgetAllocationChart = new Chart(canvas, {
                type: "doughnut",
                data: {
                    labels: rows.flatMap(row => [row.name, `${row.name} remaining`]),
                    datasets: [
                        {
                            label: "Budget",
                            data: rows.flatMap(row => [row.periodLimitCents / 100, 0]),
                            backgroundColor: rows.flatMap((_, index) => [
                                budgetChartColors[index % budgetChartColors.length],
                                "rgba(255, 255, 255, 0)"
                            ]),
                            borderWidth: 2,
                            weight: 2
                        },
                        {
                            label: "Spent",
                            data: rows.flatMap(row => {
                                const spent = Math.min(Math.max(row.spentCents, 0), row.periodLimitCents);
                                return [spent / 100, (row.periodLimitCents - spent) / 100];
                            }),
                            backgroundColor: rows.flatMap((_, index) => [
                                this.lightBudgetColor(index),
                                "rgba(255, 255, 255, 0)"
                            ]),
                            borderColor: "rgba(255, 255, 255, 0)",
                            borderWidth: 0,
                            weight: 1
                        }
                    ]
                },
                options: {
                    maintainAspectRatio: false,
                    cutout: "45%",
                    plugins: {
                        legend: {
                            position: "bottom",
                            onClick: (_event: unknown, item: { index?: number }, legend: { chart: { toggleDataVisibility: (index: number) => void; update: () => void } }) => {
                                if (item.index == null) {
                                    return;
                                }
                                const baseIndex = item.index * 2;
                                legend.chart.toggleDataVisibility(baseIndex);
                                legend.chart.toggleDataVisibility(baseIndex + 1);
                                legend.chart.update();
                            },
                            labels: {
                                generateLabels: (chart: { data: { labels?: unknown[]; datasets: Array<{ backgroundColor?: unknown }> }; getDataVisibility?: (index: number) => boolean }) => {
                                    return rows.map((row, index) => ({
                                        text: `${row.name}: ${this.money(row.spentCents)} / ${this.money(row.periodLimitCents)}`,
                                        fillStyle: this.legendColor(chart, index * 2),
                                        strokeStyle: this.legendColor(chart, index * 2),
                                        lineWidth: 1,
                                        fontColor: this.isBudgetLegendItemHidden(chart, index * 2) ? "rgba(0, 0, 0, 0.35)" : undefined,
                                        hidden: this.isBudgetLegendItemHidden(chart, index * 2),
                                        datasetIndex: 0,
                                        index
                                    }));
                                }
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: (context: { datasetIndex: number; dataIndex: number }) => {
                                    if (context.datasetIndex === 0) {
                                        if (context.dataIndex % 2 !== 0) {
                                            return "";
                                        }
                                        const row = rows[Math.floor(context.dataIndex / 2)];
                                        return `${row.name}: ${this.money(row.periodLimitCents)}`;
                                    }
                                    const row = rows[Math.floor(context.dataIndex / 2)];
                                    const isSpentSlice = context.dataIndex % 2 === 0;
                                    if (!row || !isSpentSlice) {
                                        return "";
                                    }
                                    return `${row.name} spent: ${this.money(row.spentCents)} of ${this.money(row.periodLimitCents)}`;
                                }
                            }
                        }
                    }
                }
            });
        },
        lightBudgetColor(index: number): string {
            const color = budgetChartColors[index % budgetChartColors.length];
            return color.replace("rgb(", "rgba(").replace(")", ", 0.42)");
        },
        legendColor(chart: { data: { datasets: Array<{ backgroundColor?: unknown }> }; getDataVisibility?: (index: number) => boolean }, index: number): string {
            if (this.isBudgetLegendItemHidden(chart, index)) {
                return "rgba(0, 0, 0, 0.18)";
            }
            return (chart.data.datasets[0].backgroundColor as string[])[index];
        },
        isBudgetLegendItemHidden(chart: { getDataVisibility?: (index: number) => boolean }, index: number): boolean {
            return chart.getDataVisibility ? chart.getDataVisibility(index) === false : false;
        },
        renderUnallocatedSpendChart() {
            const canvas = this.$refs.unallocatedSpendCanvas as HTMLCanvasElement | undefined;
            if (!canvas) {
                return;
            }

            const rows = this.unallocatedBudgetRows.filter(row => row.spentCents > 0);
            if (this.unallocatedSpendChart) {
                this.unallocatedSpendChart.destroy();
            }

            this.unallocatedSpendChart = new Chart(canvas, {
                type: "doughnut",
                data: {
                    labels: rows.map(row => row.name),
                    datasets: [
                        {
                            data: rows.map(row => row.spentCents / 100),
                            backgroundColor: [
                                "rgb(82, 130, 145)",
                                "rgb(166, 115, 82)",
                                "rgb(104, 139, 92)",
                                "rgb(145, 93, 103)",
                                "rgb(117, 112, 153)",
                                "rgb(153, 135, 78)"
                            ]
                        }
                    ]
                },
                options: {
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: "bottom"
                        }
                    }
                }
            });
        },
        monthNet(start: Date, end: Date): number {
            return -this.expenses
                .filter(expense => expense.date >= start && expense.date <= end)
                .reduce((total, expense) => total + expense.amount, 0);
        },
        inSelectedPeriod(expense: OutputExpense): boolean {
            if (!this.selectedPeriod) {
                return false;
            }
            const from = this.selectedPeriod.from ? new Date(this.selectedPeriod.from) : null;
            const to = this.selectedPeriod.to ? new Date(this.selectedPeriod.to) : null;
            if (to) {
                to.setHours(23, 59, 59, 999);
            }
            return (!from || expense.date >= from) && (!to || expense.date <= to);
        }
    },
    computed: {
        selectedPeriod(): InsightsPeriod | null {
            if (!this.report) {
                return null;
            }
            const label = this.selectedPeriodKind === "year" ? this.selectedYearLabel : this.selectedLabel;
            return this.report.periods.find(period => period.label === label) || this.report.periods[0] || null;
        },
        yearOptions(): string[] {
            if (!this.report) {
                return [];
            }
            return this.report.periods
                .map(period => period.label)
                .filter(label => /^\d{4}$/.test(label));
        },
        quickPeriods(): InsightsPeriod[] {
            if (!this.report) {
                return [];
            }
            return this.report.periods.filter(period => period.label === "Last 12 months" || period.label === "All time");
        },
        isYearSelected(): boolean {
            return this.selectedPeriodKind === "year";
        },
        selectedYearNumber(): number | null {
            if (!this.isYearSelected || !/^\d{4}$/.test(this.selectedYearLabel)) {
                return null;
            }
            return Number(this.selectedYearLabel);
        },
        monthlyMax(): number {
            return this.selectedPeriod
                ? Math.max(...this.selectedPeriod.monthlyTotals.map(month => month.amount), 0)
                : 0;
        },
        shoppingBucketMax(): number {
            return this.selectedPeriod
                ? Math.max(...this.selectedPeriod.shopping.buckets.map(bucket => bucket.amount), 0)
                : 0;
        },
        largeShoppingTotal(): number {
            return this.selectedPeriod
                ? this.selectedPeriod.shopping.largePurchases.reduce((acc, expense) => acc + expense.amount, 0)
                : 0;
        },
        amazonCategoryMax(): number {
            return this.selectedPeriod
                ? Math.max(...this.selectedPeriod.shopping.amazonItems.categories.map(category => category.amount), 0)
                : 0;
        },
        selectedPeriodExpenses(): OutputExpense[] {
            return this.expenses.filter(expense => this.inSelectedPeriod(expense));
        },
        selectedPeriodNet(): number {
            return -this.selectedPeriodExpenses.reduce((total, expense) => total + expense.amount, 0);
        },
        selectedPeriodIncome(): number {
            return -this.selectedPeriodExpenses
                .filter(expense => expense.amount < 0)
                .reduce((total, expense) => total + expense.amount, 0);
        },
        periodMonths(): { label: string; start: Date; end: Date }[] {
            if (!this.selectedPeriod) {
                return [];
            }
            return this.selectedPeriod.monthlyTotals.map(month => {
                const start = new Date(month.month);
                return {
                    label: this.monthLabel(month.month),
                    start,
                    end: new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999)
                };
            });
        },
        budgetMonthsByYear(): Record<number, number> {
            if (!this.selectedPeriod) {
                return {};
            }
            const from = this.selectedPeriod.from ? new Date(this.selectedPeriod.from) : null;
            const to = this.selectedPeriod.to ? new Date(this.selectedPeriod.to) : null;
            if (!from || !to) {
                return this.periodMonths.reduce((acc, month) => {
                    acc[month.start.getFullYear()] = (acc[month.start.getFullYear()] || 0) + 1;
                    return acc;
                }, {} as Record<number, number>);
            }

            const months: Record<number, number> = {};
            const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
            const end = new Date(to.getFullYear(), to.getMonth(), 1);
            while (cursor <= end) {
                months[cursor.getFullYear()] = (months[cursor.getFullYear()] || 0) + 1;
                cursor.setMonth(cursor.getMonth() + 1);
            }
            return months;
        },
        budgetProjectionRatio(): number {
            if (!this.selectedPeriod?.from || !this.selectedPeriod.to) {
                return 1;
            }

            const from = new Date(this.selectedPeriod.from);
            const to = new Date(this.selectedPeriod.to);
            const today = new Date();
            today.setHours(23, 59, 59, 999);

            if (today >= to) {
                return 1;
            }
            if (today <= from) {
                return 0;
            }

            const totalMs = to.getTime() - from.getTime();
            const elapsedMs = today.getTime() - from.getTime();
            if (totalMs <= 0) {
                return 1;
            }

            return Math.min(1, Math.max(0, elapsedMs / totalMs));
        },
        budgetRows(): BudgetRow[] {
            return this.budgetBuckets
                .filter(bucket => bucket.isActive)
                .map(bucket => {
                    const periodLimitCents = this.periodLimitForBucket(bucket.id);
                    const spentCents = this.selectedPeriodExpenses
                        .filter(this.isReportableBudgetExpense)
                        .filter(expense => expense.budgetBucket?.id === bucket.id)
                        .reduce((total, expense) => total + expense.amount, 0);
                    const remainingCents = periodLimitCents - spentCents;
                    const projectedCents = this.budgetProjectionRatio > 0 && this.budgetProjectionRatio < 1
                        ? Math.round(spentCents / this.budgetProjectionRatio)
                        : spentCents;
                    const projectedDeltaCents = periodLimitCents - projectedCents;
                    return {
                        id: bucket.id,
                        name: bucket.name,
                        slug: bucket.slug,
                        periodLimitCents,
                        spentCents,
                        projectedCents,
                        remainingCents,
                        projectedDeltaCents
                    };
                })
                .filter(row => row.periodLimitCents > 0)
                .sort((a, b) => b.periodLimitCents - a.periodLimitCents);
        },
        unallocatedBudgetRows(): BudgetRow[] {
            const rows: BudgetRow[] = this.budgetBuckets
                .filter(bucket => bucket.isActive)
                .filter(bucket => this.periodLimitForBucket(bucket.id) === 0)
                .map(bucket => {
                    const spentCents = this.selectedPeriodExpenses
                        .filter(this.isReportableBudgetExpense)
                        .filter(expense => expense.budgetBucket?.id === bucket.id)
                        .reduce((total, expense) => total + expense.amount, 0);
                    const projectedCents = this.budgetProjectionRatio > 0 && this.budgetProjectionRatio < 1
                        ? Math.round(spentCents / this.budgetProjectionRatio)
                        : spentCents;
                    return {
                        id: bucket.id,
                        name: bucket.name,
                        slug: bucket.slug,
                        periodLimitCents: 0,
                        spentCents,
                        projectedCents,
                        remainingCents: -spentCents,
                        projectedDeltaCents: -projectedCents
                    };
                })
                .filter(row => row.spentCents !== 0);

            const noBucketSpentCents = this.selectedPeriodExpenses
                .filter(this.isReportableBudgetExpense)
                .filter(expense => !expense.budgetBucket)
                .reduce((total, expense) => total + expense.amount, 0);
            if (noBucketSpentCents !== 0) {
                const projectedCents = this.budgetProjectionRatio > 0 && this.budgetProjectionRatio < 1
                    ? Math.round(noBucketSpentCents / this.budgetProjectionRatio)
                    : noBucketSpentCents;
                rows.push({
                    id: "no-bucket",
                    name: "No bucket",
                    slug: "no-bucket",
                    periodLimitCents: 0,
                    spentCents: noBucketSpentCents,
                    projectedCents,
                    remainingCents: -noBucketSpentCents,
                    projectedDeltaCents: -projectedCents
                });
            }

            return rows.sort((a, b) => b.spentCents - a.spentCents);
        },
        totalBudgetLimit(): number {
            return this.budgetRows.reduce((total, bucket) => total + bucket.periodLimitCents, 0);
        },
        hasBudgetAllocation(): boolean {
            return this.totalBudgetLimit > 0;
        },
        totalAllocatedBudgetSpend(): number {
            return this.budgetRows.reduce((total, bucket) => total + Math.max(bucket.spentCents, 0), 0);
        },
        totalUnallocatedSpend(): number {
            return this.unallocatedBudgetRows.reduce((total, bucket) => total + bucket.spentCents, 0);
        },
        totalProjectedBudgetExpenseSpend(): number {
            return this.budgetRows
                .concat(this.unallocatedBudgetRows)
                .reduce((total, bucket) => total + bucket.projectedCents, 0);
        },
        expectedIncomeCents(): number | null {
            return this.expectedIncome?.expectedIncomeCents ?? null;
        },
        expectedSavingsCents(): number | null {
            return this.expectedIncomeCents == null
                ? null
                : this.expectedIncomeCents - this.totalProjectedBudgetExpenseSpend;
        },
        hasSavingsForecast(): boolean {
            return this.isYearSelected
                && this.expectedIncomeCents != null
                && this.savingsForecastRows.length > 0;
        },
        currentSavingsForecast(): SavingsForecastRow | null {
            return this.savingsForecastRows[this.savingsForecastRows.length - 1] || null;
        },
        savingsForecastRows(): SavingsForecastRow[] {
            return this.savingsForecast?.rows || [];
        }
    }
})
